import { SignUp } from '@clerk/clerk-react'
import { useSearchParams } from 'react-router-dom'
import { MatrixRain } from '@/components/matrix-rain'
import { CLERK_APPEARANCE } from '@/components/layout/user-menu'

/**
 * Signup-only appearance: hide the Google social button (block + icon-only
 * variants) and the now-orphaned "or" divider. Google sign-in stays on the
 * LOGIN page (<SignIn> uses the shared CLERK_APPEARANCE); we remove it from
 * SIGNUP only, because a Google-FIRST signup creates an account that can't be
 * signed back into on this Clerk instance (account-linking quirk). Revert by
 * passing CLERK_APPEARANCE directly again.
 */
const SIGNUP_APPEARANCE = {
  ...CLERK_APPEARANCE,
  elements: {
    ...CLERK_APPEARANCE.elements,
    // Hide the ENTIRE social-buttons row on signup. Per-provider `__google`
    // targeting proved unreliable, so we hide the whole `socialButtons`
    // container plus the per-provider buttons and the "or" divider. `hidden!`
    // is Tailwind v4's important modifier (suffix `!`) — it forces
    // display:none over Clerk's own styles. Result: a clean email/password
    // -only signup form.
    socialButtons: 'hidden!',
    socialButtonsBlockButton__google: 'hidden!',
    socialButtonsIconButton__google: 'hidden!',
    dividerRow: 'hidden!',
  },
}

/**
 * Signup page — mirrors login-page brand treatment (see login.tsx) with a
 * slightly different headline so users know they're in the create-account
 * flow. Same background, same wordmark side, same Clerk appearance tokens.
 */
export default function SignupPage() {
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan')

  // Store selected plan so ProtectedRoute can use it for checkout.
  // 2026-06-07: Agency removed from customer-facing signup.
  if (plan && ['starter', 'pro'].includes(plan)) {
    localStorage.setItem('earlyAccessPlan', plan)
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <MatrixRain />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-grid-subtle opacity-30" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-10%] h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(28,63,255,0.22)_0%,transparent_60%)] blur-3xl"
      />

      <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-4xl flex-col items-center gap-10 md:flex-row md:items-center md:justify-center md:gap-16">
          <div className="flex max-w-sm flex-col items-center gap-5 text-center md:items-start md:text-left">
            <img
              src="/dealcloser-logo.png"
              alt="DealCloser Autopilot"
              className="h-32 w-32 rounded-2xl shadow-[0_8px_40px_rgba(28,63,255,0.18)] ring-1 ring-white/5 select-none md:h-36 md:w-36"
              draggable={false}
            />
            <h1 className="text-3xl font-bold tracking-tight text-[var(--color-silver-bright)] md:text-4xl">
              Start closing deals{' '}
              <span className="text-gradient-cobalt">on autopilot</span>.
            </h1>
            <p className="text-sm leading-relaxed text-[var(--color-silver)]">
              Create an account to connect LinkedIn, configure your ICP, and launch your first AI-run
              outreach sequence in under 10 minutes.
            </p>
          </div>

          <div className="w-full max-w-sm">
            <SignUp
              signInUrl="/login"
              fallbackRedirectUrl="/dashboard"
              appearance={SIGNUP_APPEARANCE}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
